import type { EpicDevRequestLinkResult } from '@/features/project-board/types';
import { ApiClient } from '@/shared/api/client';
import type {
  BoardIndex,
  BoardThroughput,
  DevRequest,
  DevRequestRevision,
  DocumentContent,
  DevRequestSpecLink,
  IndexSettings,
  QaScheduleDraft,
  ReindexRequested,
  ReindexScope,
} from '@/features/project-board/types';

const BASE = '/board';

export const boardApi = {
  index: () => ApiClient.getJson<BoardIndex>(BASE),
  // 처리량은 서버가 세어 준다 — 인덱스와 원천은 같지만 집계를 화면에서 반복하지 않는다.
  throughput: () => ApiClient.getJson<BoardThroughput>(`${BASE}/throughput`),
  // 경로만으로는 문서가 정해지지 않는다 — 하네스 저장소마다 `specs/…/PRD.md`가 따로 있다.
  document: (source: string, path: string) =>
    ApiClient.getJson<DocumentContent>(`${BASE}/documents?source=${encodeURIComponent(source)}&path=${encodeURIComponent(path)}`),
  settings: () => ApiClient.getJson<IndexSettings>(`${BASE}/settings`),
  // 색인을 여기서 돌리지는 않는다 — 다음 색인 실행이 무엇을 다시 읽을지 남긴다.
  requestReindex: (scope: ReindexScope, source: string | null) =>
    ApiClient.postJson<ReindexRequested>(`${BASE}/reindex`, { scope, source }),
  // 역기획서의 QA 일정을 하네스 저장소에 커밋한다. 색인은 다음 실행에 따라오므로 고친 본문을 돌려받아 바로 그린다.
  // 빈 칸은 `null`로 보낸다 — 입력 칸은 빈 문자열을 쓰지만 서버에는 "값이 없다"로 넘긴다.
  saveQaSchedule: (source: string, path: string, draft: QaScheduleDraft) =>
    ApiClient.putJson<{ text: string }>(`${BASE}/qa-schedule`, {
      source,
      path,
      ...Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, value.trim() === '' ? null : value])),
    }),
};

/**
 * 개발요청 인박스. 보드와 경로를 나눈 이유는 원천이 다르기 때문이다 — 보드는 색인이고 이쪽은
 * 슬랙에서 밀어 넣은 것이다.
 *
 * 인입 자체는 콘솔 밖(슬랙 봇)에서만 부른다. `assign`·`changePlatform`만 예외다 — 담당자 지정과 플랫폼
 * 변경은 콘솔에서 사람이 하는 조작이라 여기서 부른다.
 */
export const devRequestApi = {
  list: () =>
    ApiClient.getJson<{ requests: DevRequest[]; specs: DevRequestSpecLink[]; platforms: string[] }>('/dev-requests'),
  // 개정 이력은 상세를 펼칠 때만 받는다 — 대부분의 요청은 개정이 없고 본문이 크다.
  revisions: (id: number) => ApiClient.getJson<{ revisions: DevRequestRevision[] }>(`/dev-requests/${id}/revisions`),
  // 해제는 assignee: null로 보낸다 — 빈 문자열은 값이 있는 것으로 읽힌다.
  assign: (id: number, assignee: string | null) =>
    ApiClient.patchJson<{ assignee: string | null }>(`/dev-requests/${id}/assignee`, { assignee }),
  // 요청 문서를 적을 하네스 저장소(플랫폼)를 바꾼다. 서버가 문서를 새 저장소로 옮기고 확정한 값을 돌려준다.
  changePlatform: (id: number, platform: string) =>
    ApiClient.patchJson<{ platform: string }>(`/dev-requests/${id}/platform`, { platform }),
  // 본문 수정. 슬랙 봇이 개정을 반영하는 `:id/body`와 경로가 다르다 — 그쪽은 개정을 보낸 슬랙
  // 멤버를 함께 싣지만 콘솔에서 고치는 사람은 그 칸에 들어갈 값이 아니다.
  editBody: (id: number, body: string) =>
    ApiClient.patchJson<{ revision: number }>(`/dev-requests/${id}/content`, { body }),
};

/** 사람 세션으로 쓰며 경로는 서버가 색인 에픽에서 정한다. */
export const epicLinkApi = {
  register: (source: string, slug: string, draft: { title: string; body: string }) =>
    ApiClient.postJson<EpicDevRequestLinkResult>(EpicLinkPath.of(source, slug), draft),
  link: (source: string, slug: string, devRequestId: number, expectedDevRequestId: number | null) =>
    ApiClient.putJson<EpicDevRequestLinkResult>(EpicLinkPath.of(source, slug), { devRequestId, expectedDevRequestId }),
  unlink: (source: string, slug: string, expected: number) =>
    ApiClient.deleteJson<EpicDevRequestLinkResult>(`${EpicLinkPath.of(source, slug)}?expected=${expected}`),
};
class EpicLinkPath {
  static of(source: string, slug: string): string { return `/dev-requests/epic-links/${encodeURIComponent(source)}/${encodeURIComponent(slug)}`; }
}
